import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import './Score.scss';
import { createScore } from '../modules/vex';
import { getHighlightNoteHeadIndexes } from '../modules/tie';
import { ScoreData, ScoreDimensionConfig } from '../modules/score';
import { buildBemClassName } from '../modules/util';
import { FormFactor } from '../App';
import { useTranslation } from 'react-i18next';

// All measurements below in px, unless otherwise specified
export const scoreDimensionConfig: ScoreDimensionConfig = {
  paddingLeft: 50,
  paddingRight: 50,
  paddingTop: 40,
  paddingBottom: 40,
  maxWidth: 2500,
  systemVerticalOffset: 150,
  defaultMeasureWidth: 300,
  wholeRestCenteringOffset: 0.43, // percent
  wholeRestCenteringFirstMeasureAdditionalOffset: -0.1, // percent
  dottedWholeRestCenteringAdditionalOffset: -0.05, // percent
};

const buildClassName = buildBemClassName('c-rr-score');

export interface ScoreProps {
  scoreData: ScoreData;
  innerWidth: number;
  transitioning: boolean;
  currentFormFactor: FormFactor;
  playingNoteIndex: number | null;
  onScoreClick: () => void;
  // When true, render a non-interactive, greyed-out preview (e.g. the "next"
  // rhythm shown below the current one in auto mode).
  preview?: boolean;
}

const Score = ({
  scoreData,
  innerWidth,
  transitioning,
  currentFormFactor,
  playingNoteIndex,
  onScoreClick,
  preview = false,
}: ScoreProps) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const noteHeadsRef = useRef<NodeListOf<HTMLElement> | null>(null);
  const highlightIndexesRef = useRef<number[]>([]);

  useEffect(() => {
    const ref = containerRef;

    if (!ref || !ref.current) {
      return;
    }

    if (!scoreData.measures.length) {
      return;
    }

    const measuresPerSystem = currentFormFactor === FormFactor.DESKTOP ? 4 : 2;

    createScore(
      ref.current,
      scoreData,
      innerWidth,
      measuresPerSystem,
      scoreDimensionConfig
    );

    if (containerRef.current) {
      noteHeadsRef.current = containerRef.current.querySelectorAll(
        '.vf-notehead path'
      );
      highlightIndexesRef.current = getHighlightNoteHeadIndexes(
        scoreData.measures
      );
    }

    return () => {
      if (ref && ref.current) {
        ref.current.innerHTML = '';
      }
      noteHeadsRef.current = null;
      highlightIndexesRef.current = [];
    };
  }, [scoreData, innerWidth, currentFormFactor]);

  useEffect(() => {
    if (noteHeadsRef.current) {
      // Always clear every note first, so the highlight also resets correctly
      // when playback wraps back to the first note on a repeat.
      [...noteHeadsRef.current].forEach(
        (noteHead) => (noteHead.style.fill = '')
      );

      if (playingNoteIndex !== null) {
        // A tied note keeps the highlight on the note head that was struck,
        // rather than moving it to the tied-to head partway through.
        const noteHeadIndex =
          highlightIndexesRef.current[playingNoteIndex] ?? playingNoteIndex;

        if (noteHeadsRef.current[noteHeadIndex]) {
          noteHeadsRef.current[noteHeadIndex].style.fill = 'red';
        }
      }
    }
  }, [playingNoteIndex]);

  if (preview) {
    return (
      <div
        className={classnames(
          buildClassName()(),
          buildClassName()('preview')
        )}
        aria-hidden="true"
        data-testid="score-preview__container"
      >
        <span
          data-testid="score-preview"
          className={buildClassName('container')()}
          id="score-preview"
          ref={containerRef}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className={classnames(buildClassName()(), {
        [buildClassName()('transitioning')]: transitioning,
      })}
      onClick={onScoreClick}
      aria-hidden="true"
      title={t('clickForNewRhythm')}
      data-testid="score__button"
    >
      <span
        data-testid="score"
        className={buildClassName('container')()}
        id="score"
        ref={containerRef}
      />
    </button>
  );
};

export default Score;
